import React, { useState, useCallback, useEffect } from 'react'
import { Button, Checkbox, Icon, Message, Table } from 'semantic-ui-react'
import PropTypes from 'prop-types'
import '../SuperCutoff.css'

// Valori attesi nel campo status dei risultati di pulizia
const CR_STATUS_SUCCESS = 'success'
const CR_STATUS_ERROR = 'error'

/**
 * Step 6 — Pulizia CR non completate.
 * Carica la lista delle CR aperte. La selezione individuale non è consentita:
 * per procedere allo step successivo tutte le CR aperte devono essere eliminate.
 * L'utente conferma esplicitamente l'eliminazione di tutte le CR tramite un
 * unico checkbox master, dopodiché può lanciare la pulizia. Lo skip è disponibile
 * solo quando non ci sono CR aperte: in tal caso la lista vuota viene comunque
 * inviata al BE per marcare lo step come completato.
 *
 * Props:
 *   openCrs        {Array}     Lista CR aperte [{ pfe_id, project_code, pm_name, pm_email, status }]
 *   result         {object}    [opzionale] { processed, results: [] }
 *   onLoadCrs      {Function}  Callback per caricare la lista CR aperte
 *   onExecute      {Function}  Callback per eseguire la pulizia. Firma: fn({ pfe_ids: [] })
 *   onSkip         {Function}  Callback per saltare la pulizia quando non ci sono CR aperte
 *   loadingCrs     {boolean}   [opzionale] Default: false
 *   loadingExecute {boolean}   [opzionale] Default: false
 */
const StepCrCleaner = ({
  openCrs,
  result,
  onLoadCrs,
  onExecute,
  onSkip,
  loadingCrs = false,
  loadingExecute = false,
}) => {
  const [confirmAllChecked, setConfirmAllChecked] = useState(false)
  const [confirmVisible, setConfirmVisible] = useState(false)

  // Reset atomico del flag di conferma e del box di conferma quando l'operazione
  // è completata. Previene il bug per cui la spunta resta attiva se il parent
  // aggiorna result senza smontare il componente (es. wizard riusabile nello
  // stesso ciclo di vita).
  useEffect(() => {
    if (result) {
      setConfirmAllChecked(false)
      setConfirmVisible(false)
    }
  }, [result])

  const handleToggleAll = useCallback(() => {
    setConfirmAllChecked(prev => !prev)
  }, [])

  const handleExecute = useCallback(() => {
    // Guard contro doppio submit: il parent potrebbe non aggiornare loadingExecute prima del secondo click
    if (loadingExecute) return
    setConfirmVisible(false)
    const pfeIds = (openCrs || []).map(cr => cr.pfe_id)
    onExecute({ pfe_ids: pfeIds })
  }, [loadingExecute, onExecute, openCrs])

  const handleShowConfirm = useCallback(() => setConfirmVisible(true), [])
  const handleHideConfirm = useCallback(() => setConfirmVisible(false), [])

  // Valore derivato: CR presenti e operazione non ancora completata
  const hasCrsToProcess = Array.isArray(openCrs) && openCrs.length > 0 && !result

  // Valore derivato: risultati di dettaglio disponibili
  const hasResults = result && Array.isArray(result.results) && result.results.length > 0

  // Valore derivato: lista caricata, nessuna CR aperta trovata, operazione non ancora completata
  const noCrsFound = !result && Array.isArray(openCrs) && openCrs.length === 0

  // Numero di CR da eliminare quando hasCrsToProcess è true
  const openCrsCount = hasCrsToProcess ? openCrs.length : 0

  return (
    <div>
      <p className="supercutoff-step-description">
        Loads the list of open (incomplete) Change Requests. To proceed to the next step,
        all open CRs must be deleted. Each CR will be removed and the previous approved
        PFE/budget will be restored.
      </p>

      {/* Lista CR aperte */}
      {hasCrsToProcess && (
        <>
          <div className="supercutoff-preview-box supercutoff-preview-box-mb">
            <h4 id="open-crs-heading">Open CRs found</h4>
            <div className="supercutoff-preview-stats">
              <div className="supercutoff-stat">
                <span className="supercutoff-stat-value">{openCrsCount}</span>
                <span className="supercutoff-stat-label">Open CRs</span>
              </div>
            </div>
          </div>

          {/* Conferma esplicita: l'utente deve riconoscere che eliminerà tutte le CR
              prima che il bottone di pulizia si abiliti. */}
          <div className="supercutoff-confirm-all-row">
            <Checkbox
              checked={confirmAllChecked}
              onChange={handleToggleAll}
              disabled={loadingExecute}
              label={`Select all ${openCrsCount} open CR(s) to clean`}
              aria-label="Select all open CRs to clean"
            />
          </div>

          <div className="supercutoff-cr-table-wrapper">
            <Table celled compact="very" size="small" aria-labelledby="open-crs-heading">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>PFE ID</Table.HeaderCell>
                  <Table.HeaderCell>Project Code</Table.HeaderCell>
                  <Table.HeaderCell>PM</Table.HeaderCell>
                  <Table.HeaderCell>PM Email</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {openCrs.map(cr => (
                  <Table.Row key={cr.pfe_id} active={confirmAllChecked}>
                    <Table.Cell>{cr.pfe_id}</Table.Cell>
                    <Table.Cell>{cr.project_code}</Table.Cell>
                    <Table.Cell>{cr.pm_name}</Table.Cell>
                    <Table.Cell>{cr.pm_email}</Table.Cell>
                    <Table.Cell>
                      {/* Icona affiancata al testo per comunicare lo stato senza dipendere solo dal colore */}
                      <Icon name="warning" aria-hidden="true" />
                      <span className="supercutoff-text-warning">{cr.status}</span>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        </>
      )}

      {/* Nessuna CR aperta */}
      {noCrsFound && (
        <Message positive icon>
          <Icon name="check circle" aria-hidden="true" />
          <Message.Content>
            <Message.Header>No open CRs found</Message.Header>
            No incomplete Change Requests to clean up. You can proceed to the next step.
          </Message.Content>
        </Message>
      )}

      {/* Conferma — role="alert" annuncia il box agli screen reader all'apparizione */}
      {confirmVisible && hasCrsToProcess && (
        <div className="supercutoff-confirm-box supercutoff-confirm-box-mt" role="alert">
          <Icon name="warning sign" color="yellow" size="large" aria-hidden="true" />
          <span className="confirm-text">
            You are about to delete all <strong>{openCrsCount} open CR(s)</strong> and restore the previous
            approved PFE/budget for each. This action cannot be undone.
          </span>
        </div>
      )}

      {/* Risultato */}
      {result && (
        <>
          <Message positive icon>
            <Icon name="check circle" aria-hidden="true" />
            <Message.Content>
              <Message.Header>
                CR cleanup completed — {result.processed != null ? result.processed : '—'} processed
              </Message.Header>
            </Message.Content>
          </Message>
          {hasResults && (
            <Table celled compact="very" size="small" aria-label="Cleanup results">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>PFE ID</Table.HeaderCell>
                  <Table.HeaderCell>Project Code</Table.HeaderCell>
                  <Table.HeaderCell>Result</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {result.results.map(r => (
                  <Table.Row key={r.pfe_id} positive={r.status === CR_STATUS_SUCCESS} negative={r.status === CR_STATUS_ERROR}>
                    <Table.Cell>{r.pfe_id}</Table.Cell>
                    <Table.Cell>{r.project_code}</Table.Cell>
                    <Table.Cell>
                      {r.status === CR_STATUS_SUCCESS
                        ? (
                          <>
                            <Icon name="check" aria-hidden="true" />
                            <span>Success</span>
                          </>
                        )
                        : (
                          <>
                            <Icon name="times" aria-hidden="true" />
                            {/* Fallback esplicito: il backend potrebbe omettere il campo error */}
                            <span>{r.error || 'Errore sconosciuto'}</span>
                          </>
                        )
                      }
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </>
      )}

      <div className="supercutoff-step-actions">
        {!openCrs && !result && (
          <Button
            primary
            icon
            labelPosition="left"
            onClick={onLoadCrs}
            loading={loadingCrs}
            disabled={loadingCrs}
          >
            <Icon name="search" />
            Load open CRs
          </Button>
        )}

        {hasCrsToProcess && !confirmVisible && (
          <Button
            color="orange"
            icon
            labelPosition="left"
            onClick={handleShowConfirm}
            disabled={!confirmAllChecked || loadingExecute}
            aria-label={`Clean all ${openCrsCount} open CR(s)`}
          >
            <Icon name="trash" />
            Clean all open CRs ({openCrsCount})
          </Button>
        )}

        {hasCrsToProcess && confirmVisible && (
          <>
            <Button
              color="red"
              icon
              labelPosition="left"
              onClick={handleExecute}
              loading={loadingExecute}
              disabled={loadingExecute}
              aria-label={`Confirm cleanup of all ${openCrsCount} open CR(s)`}
            >
              <Icon name="check" />
              Confirm cleanup
            </Button>
            <Button
              basic
              onClick={handleHideConfirm}
              disabled={loadingExecute}
              aria-label="Cancel cleanup and return to confirmation"
            >
              Cancel
            </Button>
          </>
        )}

        {/* Lista vuota: l'utente conferma il proceed, che chiama /cr-clean con pfe_ids=[]
            per marcare lo step come completed lato BE e avanzare il wizard. */}
        {noCrsFound && (
          <Button
            primary
            icon
            labelPosition="left"
            onClick={onSkip}
            loading={loadingExecute}
            disabled={loadingExecute}
            aria-label="Proceed to next step"
          >
            <Icon name="arrow right" />
            Proceed to next step
          </Button>
        )}

        {result && (
          <span className="supercutoff-status-badge completed" role="status">
            <Icon name="check" aria-hidden="true" />
            Step completed
          </span>
        )}
      </div>
    </div>
  )
}

StepCrCleaner.propTypes = {
  openCrs: PropTypes.arrayOf(PropTypes.shape({
    pfe_id: PropTypes.number,
    project_code: PropTypes.string,
    pm_name: PropTypes.string,
    pm_email: PropTypes.string,
    status: PropTypes.string,
  })),
  result: PropTypes.shape({
    processed: PropTypes.number,
    results: PropTypes.arrayOf(PropTypes.shape({
      pfe_id: PropTypes.number,
      project_code: PropTypes.string,
      status: PropTypes.string,
      error: PropTypes.string,
    })),
  }),
  onLoadCrs: PropTypes.func.isRequired,
  onExecute: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  loadingCrs: PropTypes.bool,
  loadingExecute: PropTypes.bool,
}

export default StepCrCleaner
